import { DataCollectionLevel } from 'containers/Esrs/pieces/DataCollection';
import {
  AggregatedMetricsTableData,
  filterMetricLevels,
  getNestedRows,
  MetricsTableData,
  MetricType,
  useGetAggregatedMetrics,
  useGetAggregatedMetricsData,
} from '../..';
import {
  AttachmentBox_Constraint_,
  AttachmentBox_Update_Column_,
  DisclosureRequirementOwner_Constraint_,
  DisclosureRequirementOwner_Update_Column_,
  Esrs_Answer_Constraint_,
  Esrs_Answer_Update_Column_,
  Esrs_DatapointMdrm_Constraint_,
  Esrs_DatapointMdrm_Update_Column_,
  Esrs_DatapointTag_Constraint_,
  GetDatapointMdrmAnswersDocument_,
  GetDisclosureRequirementGroupsDocument_,
  GetDisclosureRequirementsOwnerDocument_,
  GetDrDatapointsTasksDocument_,
  GetDrDatapointsTasksQuery_,
  GetDrOwnerDocument_,
  GetMetricsDrQuery_,
  GetMfDisclosureRequirementGroupsDocument_,
  GetSingleEsrsMetricAnswerDocument_,
  NoteHistory_Constraint_,
  NoteHistory_Update_Column_,
  Task_Constraint_,
  Task_Update_Column_,
  TaskStatusEnum_Enum_,
  useGetRequiredBuMaterialMetricsQuery,
  useReportingUnitsMetricsPerDisclosureQuery,
  useUpdateManyTasksInProgressMutation,
  useUpsertDatapointMdrmMutation,
  useUpsertDatapointTaskMutation,
  useUpsertDisclosureRequirementsOwnerMutation,
} from 'models';
import { uniq } from 'lodash';
import { createTaskReference, isMetricStateMaterial } from 'containers/Esrs/utils';
import { separateQualitativeMetricsFromQuantitativeParents } from '../../Metrics.utils';
import { useCallback } from 'react';
import {
  mapSubRowsKeys,
  SelectedRowData,
} from '../../MetricsTable/MetricsTableComponents/SelectedRows/SelectedRows';
import { useParams } from 'react-router-dom';
import { useMemo } from 'react';
import { QUARTERS_ARRAY, TimePeriods, TimePeriodsEnums } from '../../../Requirement';

export type GeneratedSummaryStatus =
  | {
      metricRef: string;
      isApproved: boolean;
    }
  | undefined;

export type AssessableMetrics = NonNullable<
  GetMetricsDrQuery_['DisclosureRequirement_by_pk']
>['metrics'];

export const useGetMetrics = (
  withAssociation: boolean,
  companyAssessmentId: string,
  parentAssessmentId: string,
  standardRef: string,
  isBusinessUnit: boolean,
  noFilter: boolean
) => {
  const {
    metricDR,
    metrics,
    companyLevelReportingUnitId,
    projectLeader,
    isGroup,
    requiredMaterialMetrics,
    companyAnswersData,
    groupAnswersData,
    optedOutMetrics,
    dataLoading,
  } = useGetAggregatedMetricsData(
    withAssociation,
    companyAssessmentId,
    parentAssessmentId,
    isBusinessUnit,
    noFilter
  );

  const {
    aggregatedMetrics,
    qualitativeMetrics: aggregatedQualitativeMetrics,
    aggregatedMetricsAnswers,
  } = useGetAggregatedMetrics(
    metrics ?? [],
    standardRef,
    companyAssessmentId,
    companyAnswersData,
    groupAnswersData,
    isGroup
  );

  return {
    aggregatedMetrics,
    companyAnswersData,
    qualitativeMetrics: aggregatedQualitativeMetrics,
    aggregatedMetricsAnswers,
    companyLevelReportingUnitId,
    optedOutMetrics,
    dataLoading,
    projectLeader,
    requiredMaterialMetrics,
    drData: metricDR,
  };
};

export const useGetBuMetrics = (
  drRef?: string,
  companyAssessmentId?: string,
  parentAssessmentId?: string,
  isBusinessUnit?: boolean
) => {
  const { data: requiredBUMaterialMetrics, loading: requiredmmLoading } =
    useGetRequiredBuMaterialMetricsQuery({
      variables: {
        disclosureRequirementRef: drRef ?? '',
        materialStandardId: companyAssessmentId,
        parentStandardId: parentAssessmentId || companyAssessmentId,
      },
      skip: !drRef || !companyAssessmentId || !isBusinessUnit,
    });
  const requiredMetrics = useMemo(
    () =>
      uniq(requiredBUMaterialMetrics?.esrs_MaterialMetric?.map((mm) => mm.metric.reference)) ?? [],
    [requiredBUMaterialMetrics]
  );

  const { data: buData, loading } = useReportingUnitsMetricsPerDisclosureQuery({
    variables: {
      disclosureRequirementRef: drRef ?? '',
      companyAssessmentId,
      requiredMetrics: requiredMetrics,
      parentAssessmentId: parentAssessmentId || companyAssessmentId,
    },
    skip: !drRef || !companyAssessmentId || !requiredMetrics.length || !isBusinessUnit,
  });

  return {
    BUmetrics: buData?.assessableMetrics ?? ([] as AssessableMetrics),
    requiredBUMaterialMetrics: requiredBUMaterialMetrics?.esrs_MaterialMetric,
    buDataLoading: requiredmmLoading,
    loading,
  };
};

export const processBuMetrics = (
  BUmetrics: AssessableMetrics,
  companyAssessmentId: string,
  parentAssessmentId: string
) => {
  const filteredMetrics = separateQualitativeMetricsFromQuantitativeParents(
    filterMetricLevels(BUmetrics)
  );

  const nestedMetrics = filteredMetrics.map((metric) => {
    const materialStandardId = isMetricStateMaterial(
      metric.materialMetrics.find((mm) => mm.materialStandardId === companyAssessmentId)?.isMaterial
    )
      ? companyAssessmentId
      : parentAssessmentId;
    return getNestedRows({ metric, materialStandardId, isGroup: false });
  });

  return nestedMetrics;
};

export const filterDuplicateRows = (
  aggregatedMetrics: AggregatedMetricsTableData[],
  nestedBuMetrics: MetricsTableData[],
  isBusinessUnit: boolean
) => {
  const metricArray: string[] = [];
  const filterMetrics = (metric: MetricsTableData, hidden?: boolean): MetricsTableData => {
    if (metric.subRows) {
      if (metricArray.includes(metric.metric.reference) && metric.parentMetric) {
        return {
          ...metric,
          hidden: hidden,
          subRows: [
            {
              metric: metric.metric,
              referenceToSource: metric.metric?.reference,
              hidden: hidden,
              subRows: metric.subRows.map((subrow) => ({
                ...subrow,
                hidden: true,
                subRows: subrow?.subRows?.map((row) => filterMetrics(row, true)),
              })),
            },
          ],
        };
      }
      metricArray.push(metric.metric.reference);
      const subRows = metric?.subRows?.map((row) => filterMetrics(row, hidden));
      if (subRows) {
        return { ...metric, subRows: subRows, hidden: hidden };
      }
    }
    return { ...metric, hidden: hidden };
  };
  const metrics = !isBusinessUnit ? aggregatedMetrics : nestedBuMetrics;
  return [...metrics.map((metric) => filterMetrics(metric))];
};

export const getRemainingRows = (
  numberOfRows: Record<string, number>,
  metric: MetricsTableData
) => {
  const getTotalRows = (data: MetricsTableData[]): number => {
    let totalRows = data.filter((row) => !row.hidden).length;
    for (const row of data) {
      if (row.subRows && row.subRows.length > 0) {
        totalRows += getTotalRows(row.subRows);
      }
    }
    return totalRows;
  };
  const rowsCount = getTotalRows([metric]);
  const remainingCount =
    rowsCount - (numberOfRows[metric.metric?.reference ?? ''] ?? 5) > 10
      ? 10
      : rowsCount - (numberOfRows[metric.metric?.reference ?? ''] ?? 5);
  return remainingCount;
};

export const validateChildrenMetricsDataCollectionLevel = (
  dataCollectionLevel: string | null,
  isGroup: boolean,
  isBusinessUnit: boolean
) => {
  return isGroup
    ? dataCollectionLevel === DataCollectionLevel.group
    : !isBusinessUnit
      ? dataCollectionLevel === DataCollectionLevel.company
      : dataCollectionLevel === DataCollectionLevel.reportingUnits;
};

export const metricsMissingRequiredTags = (
  metrics: MetricType[],
  companyAssessmentId: string,
  isGroup: boolean,
  isBusinessUnit: boolean
) => {
  let hasMissingTags: boolean = false;
  const metricsMissingTagsList: string[] = [];

  metrics.forEach((metric) => {
    const dataCollectionLevel = metric.materialMetrics[0]?.dataCollection;
    let validateChildrenMetrics;
    const hasChildrenMetrics = metric.childrenMetrics?.length > 0;

    const materialMetric = metric.materialMetrics?.find(
      (mm) => mm.materialStandardId === companyAssessmentId
    );
    const requiredTags = metric.adminPanelTags.filter((tag) => !tag.isOptional);

    const isMissingTag = requiredTags.some((requiredTag) => {
      const matchingTag = materialMetric?.materialMetricTags?.find(
        (tag) => tag.tagType === requiredTag.type
      );
      return !matchingTag || matchingTag.materialTagValues.length === 0;
    });

    if (hasChildrenMetrics) {
      validateChildrenMetrics = metric.childrenMetrics?.some((childMetric) => {
        const childDataCollection = childMetric.childMetric?.materialMetrics?.[0]?.dataCollection;
        if (!childDataCollection) return false;

        return validateChildrenMetricsDataCollectionLevel(
          childDataCollection,
          isGroup,
          isBusinessUnit
        );
      });
    } else {
      validateChildrenMetrics = validateChildrenMetricsDataCollectionLevel(
        dataCollectionLevel ?? '',
        isGroup,
        isBusinessUnit
      );
    }

    if (isMissingTag && validateChildrenMetrics) {
      metricsMissingTagsList.push(metric.reference);
      hasMissingTags = true;
    }
  });
  return [metricsMissingTagsList, hasMissingTags] as [string[], boolean];
};

export const flattenMetricsNotAggregatable = (
  metrics: AggregatedMetricsTableData[]
): AggregatedMetricsTableData[] => {
  const result = [];

  for (const row of metrics) {
    result.push({ ...row });
    if (row.subRows && row.subRows.length > 0) {
      result.push(...flattenMetricsNotAggregatable(row.subRows));
    }
  }
  return result;
};

export const flattenMetricsWithTags = (metricsWithTags: AggregatedMetricsTableData[]) => {
  const metricsData: MetricType[] = [];

  const flattenChildrenMetric = (
    metrics: AggregatedMetricsTableData['metric']['childrenMetrics']
  ) => {
    metrics.forEach((metric) => {
      if (metric.childMetric) {
        metricsData.push(metric.childMetric);
        if (metric.childMetric?.childrenMetrics?.length > 0) {
          flattenChildrenMetric(metric.childMetric?.childrenMetrics);
        }
      }
    });
  };

  metricsWithTags.forEach((metric) => {
    metricsData.push(metric.metric);
    if (metric.metric?.childrenMetrics?.length > 0) {
      flattenChildrenMetric(metric.metric?.childrenMetrics);
    }
  });

  return metricsData;
};

export const useHandleTaskUpdate = (
  selectedRows: SelectedRowData[],
  projectLeaderId?: string,
  companyLevelReportingUnitId?: string,
  selectedDrs?: string[], // only pass if you want to change DR owner
  materialStandardId?: string, //used for DR owner
  isMfStandard?: boolean //used for DR owner
) => {
  const [upsertDatapoint] = useUpsertDatapointTaskMutation();
  const [upsertDrOwners] = useUpsertDisclosureRequirementsOwnerMutation();
  const [updatedStatusInProgress] = useUpdateManyTasksInProgressMutation();
  const { esrsAssessmentId = '', companyId = '', reportingUnitId } = useParams();
  return useCallback(
    (
      type: 'Responsible' | 'Deadline' | 'Status',
      data: {
        responsible?: string;
        deadline?: Date;
        status?: TaskStatusEnum_Enum_;
      }
    ) => {
      const mapping = {
        Responsible: ['ownerId', data.responsible],
        Deadline: ['dueDate', data.deadline],
        Status: ['status', data.status],
      } as const;
      const updateTaskColumns = {
        Responsible: Task_Update_Column_.OwnerId_,
        Deadline: Task_Update_Column_.DueDate_,
        Status: Task_Update_Column_.Status_,
      } as const;
      return upsertDatapoint({
        variables: {
          objects: selectedRows.map((dp) => ({
            timeframe: dp.timeframe,
            tagCombination: dp.tagCombination ?? 'None',
            datapointTags: {
              data: dp.tags ?? [],
              on_conflict: {
                constraint:
                  Esrs_DatapointTag_Constraint_.DatapointTagDatapointIdTagValueTagTypeKey_,
              },
            },
            answer: {
              data: {
                metricRef: dp.metricRef,
                reportingUnitId: reportingUnitId ?? companyLevelReportingUnitId,
                assessmentId: esrsAssessmentId,
                attachmentBox: {
                  data: {},
                  on_conflict: {
                    constraint: AttachmentBox_Constraint_.AttachmentBoxMetricAnswerIdKey1_,
                    update_columns: [AttachmentBox_Update_Column_.MetricAnswerId_],
                  },
                },
                noteHistory: {
                  data: {},
                  on_conflict: {
                    constraint: NoteHistory_Constraint_.NoteHistoryMetricAnswerIdKey_,
                    update_columns: [NoteHistory_Update_Column_.MetricAnswerId_],
                  },
                },
              },
              on_conflict: {
                constraint: Esrs_Answer_Constraint_.AnswerMetricRefAssessmentIdReportingUnitIdKey_,
                update_columns: [Esrs_Answer_Update_Column_.UpdatedAt_],
              },
            },
            ...(dp.mdrmRef
              ? {
                  datapointMdrms: {
                    data: [
                      {
                        mdrmRef: dp.mdrmRef,
                        task: {
                          data: {
                            table: 'DatapointMdrm',
                            companyId,

                            [mapping[type][0]]: mapping[type][1],
                            ...(type !== 'Responsible' && { ownerId: projectLeaderId }),

                            taskReference: createTaskReference({
                              assessmentId: esrsAssessmentId,
                              metricRef: dp.metricRef,
                              reportingUnitId: reportingUnitId ?? companyLevelReportingUnitId ?? '',
                              timeframe: dp.timeframe,
                              tagCombination: dp.tagCombination ?? '',
                              mdrmRef: dp.mdrmRef,
                            }),
                          },
                          on_conflict: {
                            constraint: Task_Constraint_.TaskTableTaskReferenceKey_,
                            update_columns: [updateTaskColumns[type]],
                          },
                        },
                      },
                    ],
                    on_conflict: {
                      constraint:
                        Esrs_DatapointMdrm_Constraint_.DatapointMdrmDatapointIdMdrmRefKey_,
                      update_columns: [Esrs_DatapointMdrm_Update_Column_.TaskId_],
                    },
                  },
                }
              : {
                  task: {
                    data: {
                      table: dp.mdrmRef ? 'DatapointMdrm' : 'Datapoint',
                      companyId,

                      [mapping[type][0]]: mapping[type][1],
                      ...(type !== 'Responsible' && { ownerId: projectLeaderId }),

                      taskReference: createTaskReference({
                        assessmentId: esrsAssessmentId,
                        metricRef: dp.metricRef,
                        reportingUnitId: reportingUnitId ?? companyLevelReportingUnitId ?? '',
                        timeframe: dp.timeframe,
                        tagCombination: dp.tagCombination ?? 'None',
                        mdrmRef: dp.mdrmRef,
                      }),
                    },
                    on_conflict: {
                      constraint: Task_Constraint_.TaskTableTaskReferenceKey_,
                      update_columns: [updateTaskColumns[type]],
                    },
                  },
                }),
          })),
        },
        refetchQueries: [
          GetSingleEsrsMetricAnswerDocument_,
          GetDrDatapointsTasksDocument_,
          GetDisclosureRequirementGroupsDocument_,
          GetMfDisclosureRequirementGroupsDocument_,
          GetDisclosureRequirementsOwnerDocument_,
          GetDisclosureRequirementsOwnerDocument_,
          GetDatapointMdrmAnswersDocument_,
        ],
      }).then((res) => {
        // Setting DR owners
        if (selectedDrs?.length && type === 'Responsible' && !!materialStandardId) {
          upsertDrOwners({
            variables: {
              objects: selectedDrs.map((dr) => ({
                ...(isMfStandard
                  ? { mfDisclosureRequirementRef: dr }
                  : { disclosureRequirementRef: dr }),
                materialStandardId,
                ownerId: data.responsible,
                reportingUnitId: companyLevelReportingUnitId,
              })),
              on_conflict: {
                constraint: isMfStandard
                  ? DisclosureRequirementOwner_Constraint_.DisclosureRequirementOwnerMaterialStandardIdMfDisclosureRKey_
                  : DisclosureRequirementOwner_Constraint_.DisclosureRequirementOwnerMaterialStandardIdDisclosureReqKey_,
                update_columns: [DisclosureRequirementOwner_Update_Column_.OwnerId_],
              },
            },
            refetchQueries: [
              GetDrOwnerDocument_,
              GetDisclosureRequirementGroupsDocument_,
              GetMfDisclosureRequirementGroupsDocument_,
              GetDisclosureRequirementsOwnerDocument_,
            ],
          });
        }
        // Update task status if status = 'NotStarted' and datapoint has value
        const wrongStatusdatapoints = res.data?.insert_esrs_Datapoint?.returning.filter(
          (dp) =>
            ((dp.value !== null && dp.value !== undefined) ||
              (dp.factValue !== null && dp.factValue !== undefined)) &&
            dp.task?.status === TaskStatusEnum_Enum_.NotStarted_
        );
        const wrongStatusTasks =
          wrongStatusdatapoints?.map((dp) => dp.task?.id).filter((id) => !!id) ?? [];

        if (!!wrongStatusTasks.length && type !== 'Status') {
          updatedStatusInProgress({
            variables: {
              ids: wrongStatusTasks,
            },
            refetchQueries: [GetDrDatapointsTasksDocument_],
          });
        }

        return res;
      });
    },
    [
      selectedRows,
      projectLeaderId,
      companyId,
      esrsAssessmentId,
      companyLevelReportingUnitId,
      reportingUnitId,
    ]
  );
};

export const isSpecialRow = (row: AggregatedMetricsTableData) => {
  return row.isAdditionalInfo || row.isAdditionalInfoButton || row.locked || row.referenceToSource;
};

export const getTaskReferences = (
  metric: MetricsTableData,
  selectedQuarter: TimePeriods,
  assessmentId: string,
  reportingUnitId: string
) => {
  const keys = mapSubRowsKeys(metric, selectedQuarter);

  return keys.map((row) =>
    createTaskReference({
      assessmentId,
      metricRef: row.metricRef,
      reportingUnitId,
      timeframe: row.timeframe,
      tagCombination: row.tagCombination ?? '',
    })
  );
};

export const getAllQuartersTaskReferences = (
  metrics: MetricsTableData[],
  assessmentId: string,
  reportingUnitId: string
) => {
  const keys = metrics.flatMap((m) => mapSubRowsKeys(m));

  return keys.flatMap((row) => [
    createTaskReference({
      assessmentId,
      metricRef: row.metricRef,
      reportingUnitId,
      timeframe: TimePeriodsEnums.year,
      tagCombination: row.tagCombination ?? '',
    }),
    ...QUARTERS_ARRAY.map((quarter) =>
      createTaskReference({
        assessmentId,
        metricRef: row.metricRef,
        reportingUnitId,
        timeframe: quarter,
        tagCombination: row.tagCombination ?? '',
      })
    ),
  ]);
};

export const getMetricStatus = (drTasks: GetDrDatapointsTasksQuery_['Task']) => {
  if (!drTasks?.length) return TaskStatusEnum_Enum_.NotStarted_;
  if (drTasks?.some((task) => task.status === TaskStatusEnum_Enum_.InProgress_))
    return TaskStatusEnum_Enum_.InProgress_;
  if (drTasks?.every((task) => task.status === TaskStatusEnum_Enum_.Completed_))
    return TaskStatusEnum_Enum_.Completed_;
  return TaskStatusEnum_Enum_.NotStarted_;
};
